import { useCallback } from 'react';
import { commonDataService } from '../services/commonDataService';
import { useApiResource } from '../utils/useApiResource';

/**
 * Shared team-conflicts summary (item 4) — the ONE server-side conflicts
 * computation (`GET /planning/conflicts/summary`) consumed by both the
 * Dashboard "allocation conflicts" alert and the Planning Board Conflicts
 * surfaces, so the two always show the same consultants for the same range
 * (AC-19). Definition (AC-20): a consultant conflicts when, on any working day
 * in the range (weekends, public holidays, approved absences excluded), their
 * proposed + confirmed allocation percentages sum to more than 100 (both
 * live statuses count — status never changes a figure).
 */

export interface ConflictWindowAllocation {
  id: string;
  projectId: string | null;
  /** Project name, falling back to the deal name; null for unallocated time. */
  projectName: string | null;
  percentage: number;
  status: string;
}

export interface ConflictWindow {
  /** First conflicted working day (`YYYY-MM-DD`, inclusive). */
  from: string;
  /** Last conflicted working day (`YYYY-MM-DD`, inclusive). */
  to: string;
  /** Highest single-day allocation sum inside the window. */
  peakPct: number;
  allocations: ConflictWindowAllocation[];
}

export interface ConflictConsultant {
  employeeId: string;
  firstName: string;
  lastName: string;
  /** Preferred display name once the HiBob preferred-name sync (item 6) lands. */
  displayName?: string | null;
  windows: ConflictWindow[];
}

export interface ConflictsSummary {
  consultants: ConflictConsultant[];
  totalConsultants: number;
}

/**
 * `true` when the whole range is behind us, so the endpoint has nothing it
 * could return: it omits windows that ended before today (item 1, AC-1), and
 * no window can end after the range it was scanned over does.
 *
 * Surfaces compose this into their `skip` so a wholly past range issues no
 * request at all (AC-9) rather than one whose answer is known to be empty.
 *
 * `toISOString()` on both sides deliberately, not a local-midnight helper:
 * `endDate` is sent to the server as `toISOString()` and the server keys its
 * day from that same instant, so this computes the very comparison the server
 * would. A viewer-local "today" here would let the two disagree by a day.
 */
export const conflictsRangeHasEnded = (endDate: Date): boolean =>
  endDate.toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10);

export interface UseConflictsSummaryOptions {
  /** Narrow the cohort to one department (mirrors the endpoint's filter). */
  department?: string;
  /** Suppress the fetch (e.g. the caller lacks the gating permission). */
  skip?: boolean;
}

export interface UseConflictsSummaryReturn {
  summary: ConflictsSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useConflictsSummary = (
  startDate: Date,
  endDate: Date,
  options: UseConflictsSummaryOptions = {},
): UseConflictsSummaryReturn => {
  const { department, skip } = options;

  const { data, loading, error, refetch: baseRefetch } = useApiResource<ConflictsSummary>(
    (client) => {
      const params = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      if (department) params.set('department', department);
      // Routed through the shared DedupedUrlCache so a StrictMode/remount
      // double-mount — and Dashboard + Planning Board hitting the same range —
      // coalesce onto one request (Phase 8 api-efficiency finding).
      return commonDataService.conflictsSummary.get(
        client,
        `/api/planning/conflicts/summary?${params.toString()}`,
      ) as Promise<ConflictsSummary>;
    },
    [startDate.getTime(), endDate.getTime(), department],
    { skip, errorPrefix: 'Failed to load allocation conflicts' },
  );

  // Mutation-driven refetches must hit the wire (a resolved conflict has to
  // disappear immediately) — clear the cache slots before re-running.
  const refetch = useCallback(async () => {
    commonDataService.conflictsSummary.clear();
    await baseRefetch();
  }, [baseRefetch]);

  return { summary: data, loading, error, refetch };
};
